import { SkillData } from "types/battle-engine/battle";
import {
  AnimationName,
  VALID_ANIMATIONS,
} from "types/battle-engine/animations";
import { S3_BASE_URL } from "@/utils/s3";
import { Assets, Cache } from "pixi.js";
import { Pokemon } from "prisma-layer";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";

// Track if Assets has been initialized globally
let assetsInitialized = false;

/**
 * Load an asset, using cache if available to prevent duplicate loading warnings.
 * This should be used instead of Assets.load() directly.
 */
export const loadAsset = async <T = any>(
  path: string,
  options?: object,
): Promise<T> => {
  if (Cache.has(path)) {
    return Cache.get(path) as T;
  }
  return Assets.load<T>({
    src: path,
    ...options,
  });
};

export interface AssetManifest {
  pokemonAssets: string[];
  skillAssets: string[];
  commonAssets: string[];
}

export interface LoadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Build manifest of all assets needed for battle
 */
export const buildAssetManifest = (
  teamA: Pokemon[],
  teamB: Pokemon[],
): AssetManifest => {
  const allPokemon = [...teamA, ...teamB];
  const pokemonAssets: string[] = [];
  const skillAssets: string[] = [];

  // Get all pokemon animation assets
  for (const pokemon of allPokemon) {
    const parsedPokemonName = parsePokeNameForSprite(pokemon.base_pokemon_name);

    // Add AnimData.xml
    pokemonAssets.push(
      `${S3_BASE_URL}/pokemons/${parsedPokemonName}/AnimData.xml`,
    );

    // Add animation PNGs (we'll load these dynamically after parsing XML)
    // For now, we'll handle this in the loading phase
  }

  // Get all unique skills and their projectile assets
  const allSkillNames = new Set<string>(
    allPokemon.flatMap((pokemon) => pokemon.active_skills),
  );

  const allSkills: SkillData[] = [];

  for (const skillName of allSkillNames) {
    const skill = POKEMON_SKILLS[skillName as keyof typeof POKEMON_SKILLS];
    if (!skill) throw new Error(`Skill not found: ${skillName}`);

    if (skill) {
      allSkills.push(skill);
    }
  }

  const skillAssetsSet = new Set<string>();

  // Inside any object/nested object of a skill, find text values that matches "*.png"
  for (const skill of allSkills) {
    const pngPaths = findAllPngPaths(skill);
    for (const pngPath of pngPaths) {
      skillAssetsSet.add(pngPath);
    }
  }

  skillAssets.push(...Array.from(skillAssetsSet));

  // Common assets
  const commonAssets = ["/assets/ui/hp_bg.png"];

  return {
    pokemonAssets,
    skillAssets,
    commonAssets,
  };
};

/**
 * Resets the PixiJS Assets system for a fresh battle.
 * Call this before starting a new battle to clear stale textures.
 */
export const resetBattleAssets = (): void => {
  try {
    Assets.reset();
    assetsInitialized = false;
    console.log("[AssetLoader] Assets reset complete");
  } catch (error) {
    console.warn("[AssetLoader] Error resetting assets:", error);
  }
};

/**
 * Preload all assets for battle
 * Returns progress updates via onProgress callback
 */
export const preloadBattleAssets = async (
  manifest: AssetManifest,
  onProgress?: (progress: LoadProgress) => void,
): Promise<void> => {
  // Only initialize Assets once per session
  if (!assetsInitialized) {
    await Assets.init({
      preferences: {
        crossOrigin: "anonymous",
        preferCreateImageBitmap: true,
        preferWorkers: true,
      },
    });
    assetsInitialized = true;
    console.log("[AssetLoader] Assets initialized");
  } else {
    console.log("[AssetLoader] Assets already initialized, skipping init");
  }

  // First, calculate total assets to load
  // We don't know pokemon animation count yet, so we'll estimate
  const estimatedPokemonAnimsPerPokemon = 10; // Idle, Walk, Attack, Dance, etc.
  const estimatedPokemonAnimCount =
    manifest.pokemonAssets.length * estimatedPokemonAnimsPerPokemon;

  let totalAssets =
    manifest.pokemonAssets.length + // XML files
    estimatedPokemonAnimCount + // Animation PNGs (estimated)
    manifest.skillAssets.length +
    manifest.commonAssets.length;

  let loadedAssets = 0;

  const updateProgress = () => {
    if (onProgress) {
      const percentage = Math.min((loadedAssets / totalAssets) * 100, 100);
      onProgress({
        loaded: loadedAssets,
        total: totalAssets,
        percentage,
      });
    }
  };

  // Helper to load asset only if not already cached
  const loadAssetIfNeeded = async (path: string) => {
    if (Cache.has(path)) {
      return Cache.get(path);
    }
    return Assets.load({
      src: path,
      data: {
        resourceOptions: { crossorigin: "anonymous" },
        loadOptions: { fetch: { mode: "cors", credentials: "omit" } },
      },
    });
  };

  // Load common assets first
  const commonPromises = manifest.commonAssets.map(async (path) => {
    await loadAssetIfNeeded(path);
    loadedAssets++;
    updateProgress();
  });

  await Promise.all(commonPromises);

  // Load projectile and impact assets
  const skillPromises = [
    ...manifest.skillAssets.map(async (path) => {
      await loadAssetIfNeeded(path);
      loadedAssets++;
      updateProgress();
    }),
  ];

  await Promise.all(skillPromises);

  // Load pokemon assets (XML + animations)
  // For each pokemon, we need to:
  // 1. Load XML
  // 2. Parse it to get animation names
  // 3. Load animation PNGs
  const pokemonPromises = manifest.pokemonAssets.map(async (xmlPath) => {
    // Load XML with CORS
    const xmlText = await fetch(xmlPath, { mode: "cors" })
      .then((res) => res.text())
      .catch(() => "");

    loadedAssets++;
    updateProgress();

    if (!xmlText) return;

    // Parse XML to get animation names
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    const anims = xmlDoc.querySelectorAll("Anims > Anim");

    // Extract base path from XML path
    const basePath = xmlPath.replace("/AnimData.xml", "");

    // Load all animation PNGs for this pokemon (only valid animations)
    const animPromises = Array.from(anims).map(async (anim) => {
      const animName = anim.getAttribute("Name");
      if (!animName) return;

      // Only load animations that are defined in our AnimationName enum
      if (!VALID_ANIMATIONS.includes(animName as AnimationName)) return;

      const pngPath = `${basePath}/${animName}-Anim.png`;
      try {
        await loadAssetIfNeeded(pngPath);
      } catch (error) {
        console.warn(`Failed to load animation: ${pngPath}`, error);
      }
      loadedAssets++;
      updateProgress();
    });

    // Adjust total if needed
    const actualAnimCount = animPromises.length;
    const expectedAnimCount = estimatedPokemonAnimsPerPokemon;
    totalAssets += actualAnimCount - expectedAnimCount;

    await Promise.all(animPromises);
  });

  await Promise.all(pokemonPromises);

  // Final progress update
  loadedAssets = totalAssets;
  updateProgress();
};

/**
 * Find all PNG paths in an object recursively
 */
function findAllPngPaths(obj: any): string[] {
  const results: string[] = [];

  function recurse(value: any) {
    if (
      typeof value === "string" &&
      (value.endsWith(".png") ||
        value.endsWith(".jpg") ||
        value.endsWith(".jpeg") ||
        value.endsWith(".webp") ||
        value.endsWith(".gif") ||
        value.endsWith(".svg") ||
        value.endsWith(".mp4"))
    ) {
      results.push(value);
    } else if (Array.isArray(value)) {
      for (const v of value) recurse(v);
    } else if (value && typeof value === "object") {
      for (const key in value) recurse(value[key]);
    }
  }

  recurse(obj);
  return results;
}
